import * as React from "react";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateInputPicker } from "@/components/ui/date-input-picker";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface DateRangePickerProps {
  dateRange?: DateRange;
  onDateRangeChange?: (dateRange: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  // Alternative interface for from/to
  from?: Date;
  to?: Date;
  onFromChange?: (date: Date | undefined) => void;
  onToChange?: (date: Date | undefined) => void;
}

export function DateRangePicker({ 
  dateRange, 
  onDateRangeChange, 
  placeholder = "Pick a date range",
  className,
  from,
  to,
  onFromChange,
  onToChange,
}: Readonly<DateRangePickerProps>): React.ReactElement {
  // Hook must be called unconditionally to comply with React Hooks rules
  const [open, setOpen] = React.useState(false);

  // If using from/to interface, render two DateInputPickers
  if (onFromChange !== undefined || onToChange !== undefined) {
    return (
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">From</Label>
          <DateInputPicker
            date={from}
            onDateChange={onFromChange || (() => {})}
            placeholder="DD/MM/YYYY"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">To</Label>
          <DateInputPicker
            date={to}
            onDateChange={onToChange || (() => {})}
            placeholder="DD/MM/YYYY"
          />
        </div>
      </div>
    );
  }

  // Original single-button interface

  const formatDateRange = (range: DateRange | undefined) => {
    if (!range) return placeholder;
    
    if (range.from) {
      if (range.to) {
        return `${format(range.from, 'MMM d, yyyy')} - ${format(range.to, 'MMM d, yyyy')}`;
      }
      return format(range.from, 'MMM d, yyyy');
    }
    
    return placeholder;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal bg-white dark:bg-black',
            !dateRange?.from && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange(dateRange)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={dateRange}
          onSelect={(range) => {
            onDateRangeChange?.(range);
            // Close popover when both dates are selected
            if (range?.from && range?.to) {
              setOpen(false);
            }
          }}
          numberOfMonths={2}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
